interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class ApiCache {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, PendingRequest>();
  private defaultTTL = 5 * 60 * 1000; // 5 minutes

  // Generate cache key from function name and parameters
  private getCacheKey(functionName: string, params: any[]): string {
    const paramString = params.map(p => 
      typeof p === 'object' ? JSON.stringify(p) : String(p)
    ).join('|');
    return `${functionName}:${paramString}`;
  }

  // Check if cache entry is valid
  private isValidCacheEntry(entry: CacheEntry<any>): boolean {
    const now = Date.now();
    return now - entry.timestamp < entry.expiresIn;
  }

  // Check if there's a pending request that's still valid
  private isValidPendingRequest(pending: PendingRequest): boolean {
    const now = Date.now();
    return now - pending.timestamp < 30000; // 30 seconds timeout for pending requests
  }

  async executeWithCache<T>(
    functionName: string,
    apiFunction: (...args: any[]) => Promise<T>,
    params: any[] = [],
    ttl: number = this.defaultTTL
  ): Promise<T> {
    // CACHING DISABLED - Execute API call directly without caching
    console.log(`🚀 Making fresh API call for ${functionName} (caching disabled)`);
    return apiFunction(...params);
  }

  // Clear cache for specific function
  clearCache(functionName: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${functionName}:`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Clear all cache
  clearAllCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Get cache stats for debugging
  getCacheStats(): { cacheSize: number; pendingRequests: number } {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size
    };
  }
}

export const apiCache = new ApiCache();